import {
  Activity,
  AlertTriangle,
  BookOpen,
  Cpu,
  Gauge,
  HardDrive,
  Layers,
  MemoryStick,
  Send,
  Users,
} from "lucide-react";
import { useRuns, useSystem } from "@/hooks/useEventStream";

/**
 * Slim global header strip — host pressure + worker fleet at a glance,
 * present on every authenticated page via Layout. Reads from the SSE
 * stream (`system` event every 10 s on change; `runs` for the Extracted
 * and Close chips when worker progress fires).
 */
export function SystemStatsHeader() {
  const sys = useSystem();
  const runs = useRuns();

  if (!sys) {
    return (
      <div className="border-b border-border bg-card/60 backdrop-blur-sm px-4 sm:px-6 py-1.5 text-[11px] text-muted">
        Loading system stats…
      </div>
    );
  }
  const host = sys.system;
  const totals = runs?.totals;

  return (
    <div className="border-b border-border bg-card/60 backdrop-blur-sm px-4 sm:px-6 py-1.5 flex items-center gap-4 text-[11px] overflow-x-auto whitespace-nowrap">
      <Chip
        icon={Users}
        label="Workers"
        value={sys.worker_count.toString()}
        tone={sys.worker_count > 0 ? "success" : "warn"}
        tooltip="Live dramatiq workers (heartbeat in last 30s)."
      />
      <Chip
        icon={Layers}
        label="Queue"
        value={sys.queue_size.toLocaleString()}
        tone={sys.queue_size > 1_000_000 ? "warn" : "default"}
        tooltip="Pending messages waiting for a worker."
      />
      <Chip
        icon={AlertTriangle}
        label="DLQ"
        value={sys.dlq_size.toLocaleString()}
        tone={sys.dlq_size > 0 ? "danger" : "default"}
        tooltip="Dead-letter queue: messages that exhausted all retries and are stuck."
      />
      <Sep />
      <Chip
        icon={Cpu}
        label="CPU"
        value={`${host.cpu_percent.toFixed(0)}%`}
        sub={`${host.cpu_count}c`}
        tone={host.cpu_percent > 90 ? "danger" : host.cpu_percent > 70 ? "warn" : "default"}
      />
      <Chip
        icon={MemoryStick}
        label="RAM"
        value={`${host.mem_percent.toFixed(0)}%`}
        sub={`${formatBytes(host.mem_used_bytes)}/${formatBytes(host.mem_total_bytes)}`}
        tone={host.mem_percent > 90 ? "danger" : host.mem_percent > 75 ? "warn" : "default"}
      />
      <Chip
        icon={HardDrive}
        label="Storage"
        value={`${host.disk_percent.toFixed(0)}%`}
        sub={`${formatBytes(host.disk_used_bytes)}/${formatBytes(host.disk_total_bytes)}`}
        tone={host.disk_percent > 90 ? "danger" : host.disk_percent > 75 ? "warn" : "default"}
        tooltip="MongoDB data-volume disk usage on the host (fsUsedSize / fsTotalSize from dbStats)."
      />
      <Chip
        icon={Gauge}
        label="Load"
        value={host.load_avg_1.toFixed(2)}
        sub={`${host.load_avg_5.toFixed(1)}/${host.load_avg_15.toFixed(1)}`}
        tone={host.load_avg_1 > host.cpu_count * 1.5 ? "warn" : "default"}
        tooltip="Linux 1-min load avg; 5-min and 15-min in parens. Sustained > cores × 1.5 means saturation."
      />
      {totals ? (
        <>
          <Sep />
          <Chip
            icon={Activity}
            label="Extracted"
            value={totals.extracted.toLocaleString()}
            tone="success"
          />
          <Chip
            icon={BookOpen}
            label="Indexed"
            value={totals.indexed.toLocaleString()}
            tooltip="Domains with a word-index payload built — i.e. domains whose HTML has been fetched and tokenized at least once."
          />
          <Chip
            icon={Send}
            label="Close"
            value={totals.close_links.toLocaleString()}
            tooltip="Leads mirrored from Close CRM (pushed + synced)."
          />
        </>
      ) : null}
    </div>
  );
}

function Chip({
  icon: Icon,
  label,
  value,
  sub,
  tone = "default",
  tooltip,
}: {
  icon: typeof Cpu;
  label: string;
  value: string;
  sub?: string;
  tone?: "default" | "success" | "warn" | "danger";
  tooltip?: string;
}) {
  const valueClass = {
    default: "text-fg",
    success: "text-success",
    warn: "text-warn",
    danger: "text-danger",
  }[tone];
  return (
    <span className="flex items-center gap-1.5 shrink-0" title={tooltip}>
      <Icon className="h-3.5 w-3.5 text-muted" />
      <span className="text-muted">{label}</span>
      <span className={`tabular-nums font-medium ${valueClass}`}>{value}</span>
      {sub ? <span className="text-muted/70">({sub})</span> : null}
    </span>
  );
}

function Sep() {
  return <span className="h-4 w-px bg-border shrink-0" aria-hidden="true" />;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1_048_576).toFixed(0)}MB`;
  return `${(n / 1_073_741_824).toFixed(1)}GB`;
}
